import { animate, AnimationEvent, group, keyframes, query, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { cloneDeep } from 'lodash';

export enum InlineToastPlacement {
  Left = 'left',
  Right = 'right',
}

export enum InlineToastType {
  Success = 'success',
  Error = 'error',
}

export interface InlineToastMessage {
  message: string;
  cover?: boolean;
  type?: InlineToastType;
}

export enum InlineToastTransition {
  ShowLeft = 'toastLeft',
  ShowOver = 'toastOver',
  ShowRight = 'toastRight',
}

const transitionCenterAndHide = style({
  offset: 0,
  opacity: 0,
  transform: 'translate(0, 0)',
});

// creating a copy because angular doesn't seem to be able to reuse references to the same
// AnimationStyleMetadata variables in different animation definitions
const transitionRightCenterAndHide = cloneDeep(transitionCenterAndHide);

const transitionLeftAndShow = style({
  offset: 0.2,
  opacity: 1,
  transform: 'translate(-10px, 0)',
});

const transitionLeftUpAndHide = style({
  offset: 0.85,
  opacity: 0,
  transform: 'translate(-10px, -20px)',
});

const transitionRightAndShow = style({
  offset: 0.2,
  opacity: 1,
  transform: 'translate(10px, 0)',
});

const transitionRightUpAndHide = style({
  opacity: 0,
  transform: 'translate(10px, -20px)',
  offset: 0.85,
});

const transitionBottomAndHide = style({
  opacity: 0,
  offset: 0,
  top: '100%',
});

const transitionTopAndShow = style({
  opacity: 1,
  offset: 0.5,
  top: '5px',
});

const transitionHide = style({
  opacity: 0,
});

@Component({
  selector: 'mode-inline-toast',
  templateUrl: './inline-toast.component.html',
  styleUrls: ['./inline-toast.component.scss'],
  animations: [
    trigger('showLeft', [
      transition(`* => ${InlineToastTransition.ShowLeft}`, [
        animate('1.5s', keyframes([transitionCenterAndHide, transitionLeftAndShow, transitionLeftUpAndHide])),
      ]),
    ]),
    trigger('showRight', [
      transition(`* => ${InlineToastTransition.ShowRight}`, [
        animate('1.5s', keyframes([transitionRightCenterAndHide, transitionRightAndShow, transitionRightUpAndHide])),
      ]),
    ]),
    trigger('showOver', [
      transition(`* => ${InlineToastTransition.ShowOver}`, [
        group([
          query('.toast', animate('1.5s', keyframes([transitionBottomAndHide, transitionTopAndShow]))),
          query('.inner-content', animate('.3s', transitionHide)),
        ]),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineToastComponent implements OnChanges {
  /**
   * Default placement when not covering the parent element
   */
  @Input() placement: InlineToastPlacement = InlineToastPlacement.Left;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: TS2564
  @Input() toastMessage: InlineToastMessage | undefined;

  @Output() done = new EventEmitter<void>();

  isComplete = false;

  transitions = InlineToastTransition;

  toastLeft = false;
  toastOver = false;
  toastRight = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['toastMessage'] && changes['toastMessage'].currentValue) {
      this.showToast();
    }
  }

  showToast() {
    if (this.toastMessage?.cover) {
      this.toastLeft = false;
      this.toastOver = true;
      this.toastRight = false;

      this.isComplete = false;
    } else {
      if (this.placement === InlineToastPlacement.Left) {
        this.toastLeft = true;
        this.toastOver = false;
        this.toastRight = false;
      } else {
        this.toastLeft = false;
        this.toastOver = false;
        this.toastRight = true;
      }
    }
  }

  hideInlineToast(event$: AnimationEvent) {
    if (event$.toState === 'toastLeft' || event$.toState === 'toastRight') {
      this.hideToast();
    }
  }

  hideOverToast(event$: AnimationEvent) {
    // doing this because animation.done runs whether or not the animation transition actually ran
    // need to separate real animations out from fake ones
    if (event$.toState === 'toastOver') {
      this.isComplete = true;

      this.hideToast(false);
    }
  }

  hideToast(hideMessage = true) {
    this.toastLeft = false;
    this.toastOver = false;
    this.toastRight = false;

    if (hideMessage) {
      this.toastMessage = undefined;
    }

    this.done.emit();
  }
}
